/* Owns the only button in the panel. `disabledReason` is how every
   permission-gated control is expressed: the control stays visible and
   disabled with a tooltip, so the UI shape is identical across roles. */

import { Loader2 } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { MouseEvent, ReactNode } from 'react'

import { cn } from './cn.js'
import { Tooltip } from './Tooltip.js'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md'

export interface ButtonProps {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: LucideIcon
  iconPosition?: 'left' | 'right'
  loading?: boolean
  disabled?: boolean
  /** Renders a tooltip and `aria-describedby`; implies `disabled`. */
  disabledReason?: string
  type?: 'button' | 'submit'
  onClick?: (e: MouseEvent) => void
  children: ReactNode
  className?: string
  'aria-label'?: string
  'data-testid'?: string
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-accent-solid text-accent-fg hover:bg-accent-solid-hover border border-transparent',
  secondary: 'bg-panel text-text border border-border-strong hover:bg-elevated',
  ghost: 'bg-transparent text-text-muted border border-transparent hover:bg-elevated hover:text-text',
  danger: 'bg-danger-solid text-accent-fg hover:bg-danger-solid-hover border border-transparent',
}

// Touch targets stay ≥ 44 px below md (PANEL.md §13); the min-h drops at md.
const SIZES: Record<ButtonSize, string> = {
  sm: 'min-h-11 md:min-h-7 px-2.5 text-sm gap-1.5',
  md: 'min-h-11 md:min-h-9 px-3 text-base gap-2',
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon: Icon,
  iconPosition = 'left',
  loading = false,
  disabled = false,
  disabledReason,
  type = 'button',
  onClick,
  children,
  className,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: ButtonProps): ReactNode {
  const isDisabled = disabled || loading || Boolean(disabledReason)
  const iconNode = loading ? (
    <Loader2 aria-hidden className="size-4 animate-spin" />
  ) : Icon ? (
    <Icon aria-hidden className="size-4 shrink-0" />
  ) : null

  return (
    <Tooltip content={disabledReason}>
      {({ 'aria-describedby': describedBy }) => (
        <button
          type={type}
          onClick={onClick}
          disabled={isDisabled}
          aria-busy={loading || undefined}
          aria-label={ariaLabel}
          aria-describedby={describedBy}
          data-testid={testId}
          className={cn(
            'inline-flex items-center justify-center rounded-md font-medium transition-colors',
            'disabled:cursor-not-allowed disabled:opacity-50',
            VARIANTS[variant],
            SIZES[size],
            className,
          )}
        >
          {iconPosition === 'left' && iconNode}
          <span>{children}</span>
          {iconPosition === 'right' && iconNode}
        </button>
      )}
    </Tooltip>
  )
}
